import type { ApiErrorBody } from "@themislex/shared";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly issues?: { path: string; message: string }[],
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** First validation issue for a given field path, if any. */
  fieldError(path: string): string | undefined {
    return this.issues?.find((i) => i.path === path)?.message;
  }
}

let authFailureHandler: (() => void) | null = null;
/** Called once when a refresh attempt fails — AuthProvider wires this up. */
export function setAuthFailureHandler(fn: (() => void) | null): void {
  authFailureHandler = fn;
}

interface RequestOptions {
  method?: string;
  /** JSON body (serialised automatically). */
  json?: unknown;
  /** Raw body (e.g. FormData) — pass via init.body instead of json. */
}

export async function api<T = unknown>(
  path: string,
  opts: RequestOptions & Omit<RequestInit, "body"> & { body?: BodyInit } = {},
): Promise<T> {
  const { method, json, body, headers, ...rest } = opts;

  const doFetch = (): Promise<Response> => {
    const h = new Headers(headers);
    let requestBody: BodyInit | undefined = body;
    if (json !== undefined) {
      h.set("Content-Type", "application/json");
      requestBody = JSON.stringify(json);
    }
    return fetch(path, {
      ...rest,
      method,
      body: requestBody,
      headers: h,
      credentials: "same-origin",
    });
  };

  let res = await doFetch();

  // Transparent session refresh + one retry for expired access tokens.
  if (res.status === 401 && !path.startsWith("/api/auth/")) {
    const refreshed = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "same-origin",
    });
    if (refreshed.ok) {
      res = await doFetch();
    } else {
      authFailureHandler?.();
      throw new ApiError(401, "AUTH_EXPIRED", "Your session has expired. Please sign in again.");
    }
  }

  if (!res.ok) {
    let payload: ApiErrorBody | null = null;
    try {
      payload = (await res.json()) as ApiErrorBody;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(
      res.status,
      payload?.error?.code ?? "HTTP_ERROR",
      payload?.error?.message ?? `Request failed (${res.status})`,
      payload?.error?.issues,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
